import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDeviceType } from '@cian/ui-kit';
import { useHistory, useLocation } from 'react-router-dom';

import { Scrolling, ScrollingWithControls } from '../../components/Scrolling';
import { selectTags } from '../../selectors/tags/selectTags';
import { Chip } from '../../components/Chip';
import { IApplicationState, TThunkDispatch } from '../../types/redux';
import { getTags, getTagsReset } from '../../actions/tags';
import { selectCurrentPageMeta } from '../../selectors/pagesMeta';
import { selectTagsStatus } from '../../selectors/tags/selectTagsStatus';
import { ERequestStatus } from '../../types/requestStatus';
import { Skeleton } from '../../components/Skeleton';
import { transformPageType } from './utils';
import * as s from './TagScrollingContainer.css';

/** Скроллинг тэгов для мобилки и десктопа */
export const TagScrollingContainer = () => {
  const { search, pathname } = useLocation();
  const dispatch = useDispatch<TThunkDispatch>();
  const {
    type: pageType,
    rubricId,
    pathname: currentPageMetaPathname,
  } = useSelector((state: IApplicationState) => selectCurrentPageMeta(state, pathname));
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const [activeTagId, setActiveTagId] = useState(0);
  const deviceType = useDeviceType();
  const isPhone = () => deviceType === 'phone';
  const isTablet = () => deviceType === 'tablet';
  const tags = useSelector(selectTags);
  const tagsStatus = useSelector(selectTagsStatus);
  const history = useHistory();
  const [prevRequestParams, setPrevRequestParams] = useState(`${pageType}|${rubricId}`);

  /** Запрашивает набор тэгов, если их нет */
  useEffect(() => {
    const requestParams = `${pageType}|${rubricId}`;

    if (tagsStatus !== ERequestStatus.Initial && requestParams === prevRequestParams) {
      return;
    }

    dispatch(getTags({ pageType: transformPageType(pageType), rubricId: rubricId || 0 })).finally();
    setPrevRequestParams(requestParams);
  }, [dispatch, pageType, prevRequestParams, rubricId, tagsStatus]);

  /** Очищает текущий набор тэгов */
  useEffect(() => {
    return () => {
      dispatch(getTagsReset());
    };
  }, [dispatch]);

  /** Устанавливает активный тэг */
  useEffect(() => {
    const qsWithTag = searchParams.get('with-tag');
    const activeTag = tags.find(tag => tag.name === qsWithTag);

    setActiveTagId(activeTag?.id || 0);
  }, [searchParams, tags]);

  /** Отправляет на страницу поиска по тэгам */
  const handleClick = useCallback(
    (tagName: string) => {
      searchParams.set('with-tag', tagName);
      searchParams.delete('page');
      history.push(`${currentPageMetaPathname}?${searchParams.toString()}`);
    },
    [currentPageMetaPathname, history, searchParams],
  );

  const tagsMap = tags.map(({ id, name }, index) => (
    <Chip
      key={`tag-${index}`}
      isActive={activeTagId === id}
      size={isPhone() ? 'M' : 'XS'}
      theme="stroke_secondary"
      onClick={() => handleClick(name)}
      url={`${currentPageMetaPathname}?${searchParams.toString()}`}
    >
      #{name}
    </Chip>
  ));

  return (
    <div className={s['wrapper']} data-web-ui-test-id="TagScrollingContainer">
      {tagsStatus === ERequestStatus.Loading && (
        <div className={s['skeleton-wrapper']}>
          {(isPhone()
            ? ['40%', '20%', '30%']
            : isTablet()
            ? ['20%', '10%', '20%', '10%', '20%']
            : ['20%', '10%', '15%', '10%', '20%', '13%', '10%']
          ).map((percent, index) => (
            <Skeleton key={index} width={percent} height={isPhone() ? 40 : 28} />
          ))}
        </div>
      )}

      {tagsStatus === ERequestStatus.Succeed && tags.length ? (
        isPhone() ? (
          <Scrolling>{tagsMap}</Scrolling>
        ) : (
          <ScrollingWithControls>{tagsMap}</ScrollingWithControls>
        )
      ) : null}
    </div>
  );
};
